import { getCompanyInfo, reqGetPhone } from '@/model/commonApi';
import getUrlParams from '@/plugins/get-url-params';
import { spmConfig } from '@/spm';
import utils from '@/utils';
import { handleAuth } from '@/utils/auth';
import { safeJsonStringify } from '@/utils/common';
import { isNeedAuth, loadMountDocument } from '@cfx/icorki-jwx';
import { getUser } from '@dustess/itimo-core';
import { createAdId, getAdIdParams } from '@markting/mk-create-h5-url';
import Cookies from 'js-cookie';
import UUID from 'uuidjs';
import type { App } from 'vue';

// 判断是否需要绑定手机号
const getIsNeedBindMobile = async (app: App<Element>) => {
  const { $isWxMp, $urlObj, $activityId, util, $getAuthUserInfo } = app.config.globalProperties;
  const userInfo = $getAuthUserInfo();
  (window as any).Monitor?.sendSPM(spmConfig.bindMobile.spm1, `content=${JSON.stringify({
    unionId: userInfo?.targetId,
    nickname: userInfo?.nickname,
    isReqBindMobileApi: util.storage.getItem('isReqBindMobileApi')
  })}`, true);
  if ($isWxMp && userInfo.targetId && userInfo.sessionId && $urlObj.account && !$urlObj.authPhone && !util.storage.getItem('isReqBindMobileApi')) {
    const getTelParams = {
      companyId: $urlObj.account || '',
      unionId: userInfo?.targetId || ''
    };
    (window as any).Monitor?.sendSPM(spmConfig.bindMobile.spm8);
    // 没有绑定手机号，则需要去绑定手机号
    const { isBindPhone, mobile } = await reqGetPhone(getTelParams);
    const { targetId, nickname } = userInfo || {};
    util.storage.setItem('isReqBindMobileApi', 'yes');
    // 只有微信小程序中才能授权手机号
    app.config.globalProperties.$isNeedToBindMobile = !isBindPhone && targetId;
    app.config.globalProperties.$mobile = mobile[0] || '';
    (window as any).Monitor?.sendSPM(spmConfig.bindMobile.spm2, `content=${JSON.stringify({ isBindPhone, mobile })}`);
    // 当在微信小程序里面访问时，需要设置新版本的wx sdk，用于向小程序传值
    loadMountDocument(['//res.wx.qq.com/open/js/jweixin-1.3.2.js']).then(() => {
      (window as any).Monitor?.sendSPM(spmConfig.bindMobile.spm9);
      window.wx.miniProgram.postMessage({ data: { unionid: targetId, nickname, isNotBindPhone: !isBindPhone, activityId: $urlObj.toHref ? '' : $activityId } });
    });
  }
};

// 获取是否需要登录
const getIsNeedLogin = async (app: App<Element>) => {
  const { $urlObj, util, $isWxMp, $isQwMp } = app?.config?.globalProperties || {};

  // 1. 存在B端session信息，直接return
  const toBSessionId = getUser('pc')?.sid || getUser('qw')?.sid;
  window?.Monitor?.sendSPM(spmConfig.login.spm1, `userInfo-toBSessionId: ${toBSessionId}`);
  if (toBSessionId) return false;

  // ⚠️特殊逻辑：理想汽车定制需求，无公众号授权走bizUUID
  const noAuthAccount = ['W00000013374'];
  if (noAuthAccount.includes($urlObj?.account)) {
    window?.Monitor?.sendSPM(spmConfig.login.spm1, `userInfo-auth: ${safeJsonStringify($urlObj)}`);
    await handleAuth(app.config.globalProperties, 'bizUUID');
    return false;
  }

  // 3. 小程序静默授权后，微信（非企微）小程序访问且url上存在unionId及nickName
  if ($isWxMp && !$isQwMp && $urlObj.unionId && $urlObj.nickName && $urlObj.toCSessionId) {
    const userInfo = {
      openId: $urlObj.openid || '',
      unionId: $urlObj.unionId || '',
      targetId: $urlObj.unionId || '',
      nickname: $urlObj.nickName || '',
      sessionId: $urlObj.toCSessionId || '',
      expires: 6.048e8,
      startTime: new Date().getTime(),
      targetType: 'wx-unionid' // 默认为微信unionId授权
    };
    util.storage.setItem('c_user', userInfo, localStorage);
    window?.Monitor?.sendSPM(spmConfig.login.spm1, `userInfo-c_user: ${safeJsonStringify($urlObj)}`);
  }

  // 4. 判断session是否过期，如果过期则会返回true,表明需要登录
  return await isNeedAuth();
};

// 操作dom修改ico
const changeFavico = (link: string) => {
  if (link) {
    let domFavico: HTMLLinkElement | null = document.querySelector('link[rel="icon"]');
    if (domFavico) {
      domFavico.href = link;
    }
    else {
      domFavico = document.createElement('link');
      domFavico.rel = 'icon';
      domFavico.href = link;
      document.head.appendChild(domFavico);
    }
  }
};

// 获取公司信息且设置ico
const setCorpIco = async (app: App<Element>) => {
  const { $urlObj } = app.config.globalProperties;
  const data: any = await getCompanyInfo($urlObj.corpId);
  changeFavico(data?.icon);
};

const setCookies = (urlObj: any) => {
  if (urlObj?.account) {
    window.sessionStorage.setItem('reportAccount', urlObj?.account);
    try {
      Cookies.set('dustess.aid', urlObj.account, { expires: 30 });
    } catch (error) {
      console.log(error);
    }
  }
};

/**
 * 路由参数聚合逻辑
 * @param app 组件示例，用于获取全局数据
 * @param config 路由参数 + 路由名称
 */
const queryParamsConvergence = async (app: App<Element>, config: { urlObj: any; name: any }) => {
  const { urlObj, name } = config;
  setCookies(urlObj);
  let { adId } = urlObj || {};

  const userInfo = utils.storage.getItem('c_user', localStorage);
  // 针对表单素材，判断链接中是否存在adId,如果没有adid，需要主动生成
  if (!adId && name === 'Form') {
    // saleShare 该字段在新版本mk上报里面不用了，可以不用管这个字段
    let secondScene = urlObj.saleShare === 'false' ? '1' : '0';

    if (urlObj.shareId && urlObj.shareId !== 'noId') {
      secondScene = '1';
    }

    // 当urlObj.secondScene有值时，该值从地址栏上面拿
    if (urlObj.secondScene) secondScene = urlObj.secondScene;

    const shareType = urlObj.shareType || (secondScene === '1' ? 'wxUser' : 'staff');

    const sourceId = urlObj.deliverType !== 'staffShare' ? urlObj.sourceId : undefined;
    const params: any = {
      account: urlObj.account,
      carrierId: urlObj.carrierId,
      carrierType: urlObj.carrierType,
      channel: urlObj.channel,
      deliverType: urlObj.deliverType,
      dusUserId: urlObj.qwUid,
      extraData: { ...urlObj, sourceId },
      planId: urlObj.planId,
      platform: urlObj.platform,
      qwUserId: urlObj.qwUserId,
      relationId: urlObj.materialId || urlObj.artId || urlObj.formId,
      relationType: urlObj.materialType,
      shareId: userInfo?.unionId || urlObj?.qwUid,
      shareType,
      // 如果是员工分享，那么久不传sourceId，与后端来源id矛盾
      sourceId,
      url: window.location.href,
      subDomain: 'cms',
      sceneType: 'reshare-H5'
    };
    // 如果不存在，生成adId
    try {
      const { success, data } = await createAdId(params);
      if (success) {
        adId = data?.adId;
      }
    } catch (error) {
      console.log('error: ', error);
    }
  }

  let data: any = {};

  // 如果存在，调用接口解析
  if (adId) {
    // ⚠️注意：此处是为了兼容新老数据，需同时兼容pid和account
    const targetParams = urlObj?.p_id ? { pId: Number(urlObj.p_id) } : { accountId: urlObj?.account };
    const { success, data: paramsData } = await getAdIdParams({
      adId,
      cookieId: userInfo?.targetId || UUID.generate(),
      url: window.location.href,
      ...targetParams
    } as any);
    if (success) data = paramsData;
  }

  // 目前只有广告平台使用adid，所以需要针对广告平台做兼容，埋点升级后需要重新处理
  let params = {};
  if (urlObj.adId) {
    params = {
      materialType: data.relationType,
      materialId: data.relationId,
      account: data.account,
      deliverType: data.deliverType,
      carrierType: data.carrierType,
      carrierId: data.carrierId
    };
    setCookies(data);
  }

  (window as any).Monitor?.sendSPM(spmConfig.form.spm6, `content=${JSON.stringify({ sourceId: data?.sourceId })}`);

  getUrlParams.install(app, {
    ...urlObj,
    ...params,
    ...(data?.extraData || {}),
    adId,
    sourceId: data?.sourceId || urlObj?.sourceId,
    sceneType: data?.sceneType
  });
};

// ⚠️特殊处理：针对从阿里云迁移至腾讯云的10家公司，替换当前的URL地址
/**
  W00000000152 ww54727e8ea969cbb5
  W00000000013 wwb24c7566109e6fc4
  W00000000052 wwe6f7648d2b38f8e8
  W00000000059 ww53beba8466550e31
  W00000000111 wwc92b6bb947b71cc4
  W00000000118 ww07fe6248d8d44bab
  W00000000128 ww28d3052cae54754e
  W00000000131 wwf95dac1f930c9940
  W00000000161 wwa85c579beb0a32f1
  W00000000223 ww93c54f3a7885b6d2
  W00000000001 wwb5c29ccdcdb6e1b5
 */
const redirectHref = (app: App<Element>) => {
  const { $urlObj, $isMp } = app.config.globalProperties;
  const targetCorpID = $urlObj?.corpId;
  let targetOrigin = window.location.origin;
  if ([
    'ww54727e8ea969cbb5', 'wwb24c7566109e6fc4', 'wwe6f7648d2b38f8e8',
    'ww53beba8466550e31', 'wwc92b6bb947b71cc4', 'ww07fe6248d8d44bab',
    'ww28d3052cae54754e', 'wwf95dac1f930c9940', 'wwa85c579beb0a32f1',
    'ww93c54f3a7885b6d2', 'wwb5c29ccdcdb6e1b5'
  ].includes(targetCorpID) && targetOrigin.includes('-tx') && targetCorpID && !$isMp) {
    targetOrigin = targetOrigin.replace('-tx', '');
    const targetHref = `${targetOrigin}${window.location.pathname}${window.location.hash}`;
    window.location.replace(targetHref);
    return true;
  }
  return false;
};

export {
  getIsNeedBindMobile,
  getIsNeedLogin, queryParamsConvergence,
  redirectHref, setCorpIco
};

